// Builds the `campaignTabConfiguration` / `campaignColumnConfiguration` arrays
// for the /updateCampaignDetails payload from the Add Campaign step's live UI
// state (`tabConfiguration` + `columnConfigurations`).
//
// Why this exists: the edit-save (ReviewAndAddProject) used to read the
// already-built `campaignTabConfiguration` / `campaignColumnConfiguration`
// arrays off the campaign step data. But the tab/column toggles only mutate
// `tabConfiguration` / `columnConfigurations`, so edits never reached the
// payload (and the original arrays got wiped once any toggle fired). Rebuilding
// from the toggled state at save time is what makes edits actually persist.
//
// Single-selection model: the UI exposes ONE on/off toggle, backed by
// `systemView` (the reference / source of truth). Overall View is retired
// from this payload entirely — no viewTab: "overallView" tab rows are sent,
// and isOverallViewSelected is fixed at `false` rather than mirroring
// systemView — since its own toggle UI is hidden and nothing reads it back.
// Existing Overall View data already in Corda is simply left untouched; we
// can't delete those columns/rows, so we've stopped writing to them instead.

const resolveNewName = (renamed) =>
  renamed && renamed !== "NA" ? renamed : null;

const joinStages = (usedInStage) =>
  Array.isArray(usedInStage) ? usedInStage.join(",") : usedInStage || "";

// Builds `campaignBOMDetails` from the two live material tables. This exists for
// the same reason as the config builders above: the Selection toggles only
// mutate `materialsNotProvidedByBMS` / `materialsProvidedByBMS`, and the
// edit-save path was sending the untouched `campaignBOMDetails` array it had
// fetched, so toggling a material never reached the backend. Both save paths now
// build from the toggled state.
export const buildCampaignBOMDetails = (
  campaignData = {},
  { collabrixCampaignID = null, collabrixProjectID = null } = {}
) => {
  const cid = collabrixCampaignID ?? campaignData.collabrixCampaignID ?? null;

  // `selected` is undefined on rows that predate it being mapped in, so an
  // explicit `false` is the only thing that means "toggled off".
  const isActiveOf = (material) => material.selected !== false;

  const notProvided = (campaignData.materialsNotProvidedByBMS || []).map(
    (material) => ({
      collabrixCampaignID: cid,
      collabrixBomID: material.collabrixBomID || null,
      collabrixProjectID,
      cmoMaterialCode: material.cmoMaterialCode || "",
      cmoMaterialDescription: material.cmoMaterialDescription || "",
      cmoStageIDs: joinStages(material.usedInStage),
      cmoUOM: material.cmoUOM || "",
      supplierName: material.supplier || "",
      countryOfOrigin: material.countryOfOrigin || "",
      isActive: isActiveOf(material),
      sponsorMaterialCode: material.bo_material_code || "",
      sponsorMaterialDescription: material.material_description || "",
      sponsorUOM: material.bo_material_UOM || "",
      materialProvidedBySponsor: false,
    })
  );

  const provided = (campaignData.materialsProvidedByBMS || []).map(
    (material) => ({
      collabrixCampaignID: cid,
      collabrixBomID: material.collabrixBomID || null,
      collabrixProjectID,
      cmoMaterialCode: material.cmoMaterialCode || "",
      cmoMaterialDescription: material.cmoMaterialDescription || "",
      cmoStageIDs: joinStages(material.usedInStage),
      cmoUOM: material.cmoUOM || "",
      supplierName: material.supplierName || "",
      countryOfOrigin: material.countryOfOrigin || "",
      isActive: isActiveOf(material),
      sponsorMaterialCode: material.bmsMaterialCode || "",
      sponsorMaterialDescription: material.bmsMaterialDescription || "",
      sponsorUOM: material.bmsUOM || "",
      materialProvidedBySponsor: true,
    })
  );

  return [...notProvided, ...provided];
};

// Rebuild from the live material tables when the campaign step has them,
// otherwise fall back to the fetched array (pristine edit, step never mounted).
// Keyed on the arrays existing rather than being non-empty, so deleting the last
// material doesn't resurrect the fetched rows.
export const resolveCampaignBOMDetails = (campaignData = {}, options) => {
  if (
    campaignData.materialsNotProvidedByBMS ||
    campaignData.materialsProvidedByBMS
  ) {
    return buildCampaignBOMDetails(campaignData, options);
  }
  return campaignData.campaignBOMDetails || [];
};

export const buildCampaignConfigArrays = (campaignData = {}, collabrixCampaignIDOverride) => {
  // The config comes from the per-CMO template (fetchCampaignMgmtTabs/Columns),
  // which carries no campaign id — so callers pass the resolved id in. Fall back
  // to whatever the step data holds only when no override is given.
  const collabrixCampaignID =
    collabrixCampaignIDOverride ?? campaignData.collabrixCampaignID ?? null;
  const tabCfg = campaignData.tabConfiguration || {};
  const colCfg = campaignData.columnConfigurations || {};

  const systemViewTabs = tabCfg.systemView || [];

  const campaignTabConfiguration = [];

  // Overall View is not sent at all. Its UI is hidden (never user-editable),
  // nothing reads it back, and the middleware only touches rows it's actually
  // given (controllers/BMS/updateCampaignDetails.js loops the incoming array —
  // no sweep/cleanup of anything left out) — so simply not sending a viewTab:
  // "overallView" row here means any already-existing overallView tab state in
  // Corda is left exactly as it is, untouched and un-updated, forever. New
  // tabs never get an overallView state created for them at all. We can't
  // delete existing overallView rows, but we don't need to keep writing to
  // them either — this is that: stop maintaining it, not "mirror it".

  systemViewTabs.forEach((tab) => {
    campaignTabConfiguration.push({
      collabrixCampaignID,
      // This array now holds every tab, since System View is the only table
      // shown. A tab stored under a different viewTab keeps it, so the save
      // updates its own row instead of creating a duplicate one.
      viewTab: tab.viewTab || "systemView",
      // Custom tabs now sit in this array so they render; tabType keeps them
      // marked. Tabs added in the UI carry no tabType and stay "default".
      tabType: tab.tabType === "custom" ? "custom" : "default",
      tabName: tab.name || "",
      newTabName: resolveNewName(tab.renamedTab),
      isActive: tab.selected || false,
      markForDelete: tab.markForDelete || false,
      collabrixTabConfigID: tab.collabrixTabConfigID ?? null,
    });
  });

  // Overall View defaults stay unsent (see above), but a custom overall-view tab
  // reached this payload via customTabs before, so keep sending it.
  (tabCfg.overallView || []).forEach((tab) => {
    if (tab.tabType !== "custom") return;
    campaignTabConfiguration.push({
      collabrixCampaignID,
      viewTab: "overallView",
      tabType: "custom",
      tabName: tab.name || "",
      newTabName: resolveNewName(tab.renamedTab),
      isActive: tab.selected || false,
      markForDelete: tab.markForDelete || false,
      collabrixTabConfigID: tab.collabrixTabConfigID ?? null,
    });
  });

  // Kept for saved step data that still holds the old bucket.
  (tabCfg.customTabs || []).forEach((tab) => {
    campaignTabConfiguration.push({
      collabrixCampaignID,
      viewTab: tab.viewTab || "overallView",
      tabType: "custom",
      tabName: tab.name || "",
      newTabName: resolveNewName(tab.renamedTab),
      isActive: tab.selected || false,
      markForDelete: tab.markForDelete || false,
      collabrixTabConfigID: tab.collabrixTabConfigID ?? null,
    });
  });

  // A column's tab id MUST come from the tab it is grouped under, not from the
  // column's own (possibly stale/empty) value — otherwise every column gets
  // written under one tab (the bug where all columns landed under "Documents").
  const tabNameToId = {};
  [...systemViewTabs, ...(tabCfg.overallView || []), ...(tabCfg.customTabs || [])].forEach((t) => {
    if (t?.name && t.collabrixTabConfigID != null && tabNameToId[t.name] == null) {
      tabNameToId[t.name] = t.collabrixTabConfigID;
    }
  });

  const campaignColumnConfiguration = [];
  Object.keys(colCfg).forEach((tabName) => {
    const cols = colCfg[tabName]?.columns || [];
    // Correct tab id for THIS group (fall back to the column's own only if the
    // tab has no id yet — e.g. a brand-new campaign before tabs are persisted).
    const tabId = tabNameToId[tabName] ?? null;
    cols.forEach((column) => {
      campaignColumnConfiguration.push({
        collabrixCampaignID,
        // Name of the tab this column belongs to — lets the backend link the
        // column to the right tab even when collabrixTabConfigID isn't known
        // yet (new campaign). Harmless if the backend ignores it.
        tabName,
        columnName: column.name || "",
        newColumnName: resolveNewName(column.renamedTab),
        // isOverallViewSelected can't be left out — it's a required boolean
        // field on this same ColumnConfiguration row, not a separate state we
        // could just not send (unlike the tab rows above). Since nothing reads
        // it back, it's fixed at `false` rather than mirroring systemView, so
        // it reads as plainly unused instead of looking like a live second
        // copy of the real selection.
        isOverallViewSelected: false,
        isSystemViewSelected: column.systemView || false,
        // Persisted (seed) columns keep an ID -> "Default"; user-added ones
        // have none yet -> "New".
        columnType: column.collabrixColumnConfigID ? "Default" : "New",
        // Deleting an already-saved column must reach the backend, otherwise it
        // reappears on the next load. handleDeleteColumn flags saved columns
        // instead of dropping them locally, and that flag is forwarded here.
        markForDelete: column.markForDelete === true,
        collabrixTabConfigID: tabId ?? column.collabrixTabConfigID ?? null,
        collabrixColumnConfigID: column.collabrixColumnConfigID ?? null,
      });
    });
  });

  return { campaignTabConfiguration, campaignColumnConfiguration };
};

// Resolve the config arrays for a save: rebuild from the live toggled state when
// present, otherwise fall back to whatever arrays are already on the step data
// (pristine edit with no changes made).
export const resolveCampaignConfigArrays = (campaignData = {}, collabrixCampaignIDOverride) => {
  const cid =
    collabrixCampaignIDOverride ?? campaignData.collabrixCampaignID ?? null;

  let arrays;
  if (campaignData.tabConfiguration || campaignData.columnConfigurations) {
    arrays = buildCampaignConfigArrays(campaignData, cid);
  } else {
    arrays = {
      campaignTabConfiguration: campaignData.campaignTabConfiguration || [],
      campaignColumnConfiguration: campaignData.campaignColumnConfiguration || [],
    };
  }

  // Guarantee every object carries the campaign id (the fallback arrays and the
  // config templates may not have it set).
  if (cid != null) {
    arrays = {
      campaignTabConfiguration: arrays.campaignTabConfiguration.map((o) => ({
        ...o,
        collabrixCampaignID: o.collabrixCampaignID || cid,
      })),
      campaignColumnConfiguration: arrays.campaignColumnConfiguration.map((o) => ({
        ...o,
        collabrixCampaignID: o.collabrixCampaignID || cid,
      })),
    };
  }

  return arrays;
};
